import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { actionColorMap, actionIconMap } from '@/constants/log-action'
import type { DashboardRecentActivity } from '@/features/dashboard/types/dashboard'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { useUI } from '@/hooks/useUI'
import { parseLogNote } from '@/utils/log'
import { Clock } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export function ActivityFeed({
  entries = [],
  isLoading = false,
}: {
  entries?: DashboardRecentActivity[]
  isLoading?: boolean
}) {
  const { t } = useTranslation()
  const { logActions, deliveryMethods, getEnumLabel } = useUI()
  const { formatDateTime } = useDateFormatter()
  const recentLogs = [...entries].sort((a, b) => (b.dateCreated ?? '').localeCompare(a.dateCreated ?? '')).slice(0, 15)

  return (
    <Card className="border-border/50 bg-card" data-testid="dashboard-activity-feed">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium text-foreground">{t('dashboard.latestActivity')}</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex flex-col gap-3">
          {isLoading && (
            <div className="flex items-center justify-center py-8 text-xs text-muted-foreground">
              <Clock className="size-4 animate-spin" />
            </div>
          )}

          {!isLoading &&
            recentLogs.map(log => {
              const Icon = actionIconMap[log.action] || Clock
              const colorClass = actionColorMap[log.action] || 'text-muted-foreground'
              const notes = parseLogNote(log.note)
              return (
                <div key={log.id} className="flex items-start gap-3">
                  <div
                    className={`mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md bg-secondary ${colorClass}`}
                  >
                    <Icon className="size-3.5" />
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-foreground">
                        {getEnumLabel(logActions, log.action, log.action)}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatDateTime(log.dateCreated, {
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                    <span className="truncate text-xs text-muted-foreground">{log.itemSerialNumber}</span>
                    {notes.map((part, index) => (
                      <p key={index} className="text-xs text-muted-foreground">
                        {part.type === 'method'
                          ? `${t('requests.method')}: ${getEnumLabel(deliveryMethods, part.value, part.value)}`
                          : part.value}
                      </p>
                    ))}
                    <span className="text-xs text-muted-foreground/70">
                      {log.createdByName || t('dashboard.system')}
                    </span>
                  </div>
                </div>
              )
            })}

          {!isLoading && recentLogs.length === 0 && (
            <p className="py-4 text-center text-xs text-muted-foreground">{t('dashboard.noActivity')}</p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
